import { useState } from 'react'
import { AuditChart, SuccessRateChart, TaskPerformanceChart } from './ResearchCharts'
import VideoLibrary from './VideoLibrary'

const asset = (path: string) => `${import.meta.env.BASE_URL}${path}`
const arxivUrl = 'https://arxiv.org/abs/2607.25731'
const arxivPdfUrl = 'https://arxiv.org/pdf/2607.25731'

const citation = `@article{zhao2026trimanpolicy,
  title   = {Tri-Manual Visuomotor Imitation Learning of Robot Policies},
  author  = {Zhao, James and Ba, Mingyuan and Zhi, Weiming},
  journal = {arXiv preprint arXiv:2607.25731},
  year    = {2026}
}`

const paperAbstract = `Bimanual teleoperation provides an effective way to collect robot demonstrations, but it assumes that the operator and robot have matching numbers of simultaneous control channels. This assumption breaks for tri-manual systems: the robot can coordinate three arms concurrently, whereas a single operator can continuously control only two. Pairwise mode switching may therefore record otherwise independent motions sequentially, causing behaviour cloning to reproduce delays imposed by the interface rather than required by the task. We present TriManPolicy, a tri-manual imitation learning system that allows one operator to demonstrate behaviours for three arms. Its central component is Dependency-Aware Tri-Arm Scheduling (DATS). The key idea is to preserve the demonstrated arm motions while reconsidering when they occur. DATS retimes demonstrations offline by preserving local sensorimotor segments of fixed duration and repositioning them according to constraints on task order and arm usage that are reviewed by a human. The resulting data train a single synchronous policy for all three arms, while deployment requires neither the dependency graph nor the scheduler. Across six challenging tasks performed in the real world, policies trained on demonstrations retimed by DATS exhibit more efficient coordination while maintaining comparable observed task success. Offline analysis further shows that DATS changes the supervision across arms rather than merely removing idle periods.`

function App() {
  const [copied, setCopied] = useState(false)

  const copyCitation = async () => {
    await navigator.clipboard.writeText(citation)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <main>
      <nav className="site-nav" aria-label="Project navigation">
        <a className="nav-origin" href="https://aus.bot/research/">
          <span>PAIR Lab</span><span aria-hidden="true">/</span><span>Research</span>
        </a>
        <div className="nav-links">
          <a href="#method">Method</a>
          <a href="#results">Results</a>
          <a href="#videos">Videos</a>
          <a href="#paper">Paper</a>
          <a href={asset('evidence.html')}>Evidence</a>
        </div>
      </nav>

      <header className="hero page-shell">
        <h1><span>TriManPolicy:</span> Tri-Manual Visuomotor Imitation Learning of Robot Policies</h1>

        <div className="author-block">
          <p className="authors">
            <span>James Zhao<sup>1,†</sup></span>
            <span>Mingyuan Ba<sup>1,†</sup></span>
            <span>Weiming Zhi<sup>1,2,3,*</sup></span>
          </p>
          <div className="affiliations">
            <p><sup>1</sup> School of Computer Science and <sup>2</sup> Australian Center For Robotics, The University of Sydney, Australia</p>
            <p><sup>3</sup> College of Connected Computing, Vanderbilt University, TN, USA</p>
          </div>
          <p className="author-notes"><sup>†</sup> Equal contribution · <sup>*</sup> Corresponding author: <a href="mailto:Weiming.Zhi@sydney.edu.au">Weiming.Zhi@sydney.edu.au</a></p>
        </div>

        <div className="hero-actions" aria-label="Project links">
          <a className="primary-action" href={arxivPdfUrl} target="_blank" rel="noreferrer">Paper <span aria-hidden="true">↗</span></a>
          <a href={arxivUrl} target="_blank" rel="noreferrer">arXiv <span aria-hidden="true">↗</span></a>
          <a href="#videos">Videos <span aria-hidden="true">↓</span></a>
          <a href="#citation">BibTeX <span aria-hidden="true">↓</span></a>
        </div>
      </header>

      <figure className="hero-media hero-video-shell">
        <video autoPlay muted loop playsInline controls poster={asset('media/hero-poster.webp')}>
          <source src={asset('media/hero.mp4')} type="video/mp4" />
        </video>
        <figcaption><span>Autonomous synchronous deployment</span><span>Three robot arms · one policy</span></figcaption>
      </figure>

      <section className="abstract-section page-shell" aria-labelledby="abstract-title">
        <h2 id="abstract-title">Abstract</h2>
        <p>{paperAbstract}</p>
      </section>

      <section className="method section-band" id="method" aria-labelledby="method-title">
        <div className="page-shell section-intro split-heading">
          <h2 id="method-title">Demonstration time is not always task time.</h2>
          <p>A single operator continuously controls two arms, while the deployed policy commands all three. DATS preserves demonstrated local motion while revising graph-permitted global placement before behaviour cloning.</p>
        </div>

        <figure className="method-figure method-media">
          <img src={asset('media/method.png')} width="1964" height="1506" alt="TriManPolicy pipeline from pairwise demonstrations to a synchronous three-arm policy" />
          <figcaption>Pairwise demonstrations are segmented, reviewed against task dependencies, retimed, and reconstructed as ordinary synchronous training sequences.</figcaption>
        </figure>

        <div className="method-steps page-shell">
          <article><h3>Collect in pairs</h3><p>One operator switches between arm pairs while demonstrating a task involving all three arms.</p></article>
          <article><h3>Review dependencies</h3><p>Each segment retains its duration, required arms, local sensorimotor stream and checked predecessors.</p></article>
          <article><h3>Retiming before BC</h3><p>Compatible segments are repositioned globally, changing which cross-arm targets share an action horizon.</p></article>
        </div>
      </section>

      <section className="results page-shell" id="results" aria-labelledby="results-title">
        <div className="section-intro split-heading">
          <h2 id="results-title">Faster execution across six tasks.</h2>
          <p>Matched Raw and DATS policies use the same demonstrations and learner. DATS changes only the temporal organisation of supervision.</p>
        </div>

        <div className="metrics" aria-label="Experimental summary">
          <article><strong>42.5%</strong><span>lower macro-mean successful-trial completion time</span></article>
          <article><strong>6</strong><span>real-robot tasks with complementary tri-arm roles</span></article>
          <article><strong>300</strong><span>physical trials across Raw and DATS policies</span></article>
        </div>
      </section>

      <section className="videos section-band" id="videos" aria-labelledby="videos-title">
        <div className="page-shell section-intro split-heading">
          <h2 id="videos-title">Raw and DATS policies, side by side.</h2>
          <p>Compare autonomous rollouts trained from the collected timeline with policies trained from DATS supervision. Select a task, then move between recorded trials.</p>
        </div>
        <div className="page-shell">
          <VideoLibrary />
        </div>
      </section>

      <section className="results-evidence page-shell" aria-label="Quantitative results">
        <div className="chart-block">
          <div className="chart-heading"><h3>Completion time falls in every task.</h3><p>Mean ± sample SD over successful rollouts. Success counts remain level or improve for every task.</p></div>
          <TaskPerformanceChart />
          <div className="success-block">
            <div className="success-heading">
              <h4>Observed success remains stable.</h4>
              <p>DATS records an equal or higher number of successful trials in every task: 129/150 overall, compared with 126/150 for Raw.</p>
            </div>
            <SuccessRateChart />
          </div>
        </div>

        <div className="chart-block audit-block">
          <div className="chart-heading audit-heading">
            <h3>Compression and composition are distinct.</h3>
            <p>Gap removes intervals in which no annotated segment is active, while preserving the demonstrated order and overlap. Each point compares DATS with Gap for one demonstration.</p>
          </div>
          <div className="audit-reading" aria-label="How to read the audit chart">
            <article>
              <span>X axis</span>
              <h4>Compression</h4>
              <p>The plotted value is Gap minus DATS duration for the same episode. Move right when DATS is shorter beyond idle removal.</p>
            </article>
            <article>
              <span>Y axis</span>
              <h4>Composition</h4>
              <p>Coverage is the fraction of graph-compatible, disjoint-arm pairs that share at least one 100-step, 2.0-second target window. Move up as that fraction grows.</p>
            </article>
          </div>
          <AuditChart />
          <div className="audit-findings" aria-label="Audit findings">
            <article className="audit-total">
              <strong>237 / 237</strong>
              <p>Every processed demonstration gains co-window coverage after DATS.</p>
            </article>
            <article>
              <h4>Shorter and broader</h4>
              <p>TowelHang, BinTowel, BagTape and LidEraser move right and up: DATS shortens the timeline and broadens the joint targets available during training.</p>
            </article>
            <article>
              <h4>Broader without shorter</h4>
              <p>ToteCards is 0.8 seconds longer than Gap on average, yet coverage rises from 0% to 94.0%. TrayWipe changes by 0.1 seconds while coverage rises from 9.5% to 71.2%.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="paper page-shell" id="paper" aria-labelledby="paper-title">
        <div className="section-intro">
          <h2 id="paper-title">Paper and citation</h2>
        </div>
        <div className="paper-grid">
          <div>
            <p className="paper-abstract">Read the full preprint for the method, experiments and analysis. The evaluation page provides a concise record of the study design, main results and evidence scope.</p>
            <div className="paper-links">
              <a className="text-link" href={arxivPdfUrl} target="_blank" rel="noreferrer">Read the paper <span aria-hidden="true">↗</span></a>
              <a className="text-link" href={arxivUrl} target="_blank" rel="noreferrer">arXiv:2607.25731 <span aria-hidden="true">↗</span></a>
              <a className="text-link" href={asset('evidence.html')}>Evaluation evidence <span aria-hidden="true">↗</span></a>
            </div>
          </div>
          <div className="bibtex-wrap" id="citation">
            <button type="button" onClick={copyCitation}>{copied ? 'Copied' : 'Copy BibTeX'}</button>
            <pre><code>{citation}</code></pre>
          </div>
        </div>
      </section>

      <footer className="site-footer page-shell">
        <p><strong>TriManPolicy</strong><br />PAIR Lab · The University of Sydney</p>
        <div><a href={asset('evidence.html')}>Evidence notes</a><a href="https://aus.bot/research/">PAIR research</a><a href="mailto:weiming.zhi@sydney.edu.au">Contact</a></div>
      </footer>
    </main>
  )
}

export default App
